import React from "react";
import Odometer from "odometer";
import { useTranslation } from "../localization";

const HISTORY_VIEW_TRANSITION_MS = 420;
const HIGH_BID_HOLD_MS = 1000;
const HIGH_BID_FADE_OUT_MS = 260;
const INITIAL_GAME_FLASH_REPETITIONS = 2;
const INITIAL_GAME_FLASH_PASS_MS = 2500;
const INITIAL_GAME_FLASH_MIN_STEP_MS = 60;

const formatBidAmount = (amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "";
  }

  return numericAmount < 1
    ? `${Math.round(numericAmount * 100)}¢`
    : `$${numericAmount.toFixed(2)}`;
};

const OdometerValue = ({ value, format = "(,ddd)", duration = 900 }) => {
  const elementRef = React.useRef(null);
  const odometerRef = React.useRef(null);
  const previousDigitCountRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const numericValue = Number(value ?? 0);
    const nextValue = Number.isFinite(numericValue) ? numericValue : 0;
    const digitCount = Math.trunc(Math.abs(nextValue)).toString().length;
    const lostDigit =
      previousDigitCountRef.current !== null &&
      digitCount < previousDigitCountRef.current;

    if (!odometerRef.current) {
      odometerRef.current = new Odometer({
        el: element,
        value: nextValue,
        format,
        duration,
        theme: "default",
      });
    } else if (lostDigit) {
      odometerRef.current.value = nextValue;
      odometerRef.current.render();
    } else {
      odometerRef.current.update(nextValue);
    }

    previousDigitCountRef.current = digitCount;
  }, [value, format, duration]);

  return <span ref={elementRef} />;
};

const MoneyOdometerValue = ({ amount, duration = 900 }) => {
  const totalCents = Math.max(0, Math.round((Number(amount) || 0) * 100));

  if (totalCents < 100) {
    return (
      <>
        <OdometerValue value={totalCents} duration={duration} />
        <span className="money-suffix">¢</span>
      </>
    );
  }

  const dollars = Math.floor(totalCents / 100);
  const cents = totalCents % 100;

  return (
    <>
      <span className="money-prefix">$</span>
      <OdometerValue value={dollars} duration={duration} />
      <span className="money-radix">.</span>
      <span className="fixed-cents-odometer">
        <OdometerValue value={100 + cents} duration={duration} />
      </span>
    </>
  );
};

const BidDistributionChart = ({
  bidSummary,
  hasCurrentGameBidDistribution = false,
  lowestWinningBid,
  potTotal,
  currentBidAmount,
  isNewGame,
  isBidPlaced,
  lastBidPlaced,
  lastBidGameId,
  submittedBid,
  pendingNextGameBid,
  currentGame,
  displayGameId,
  onBidSubmit,
  balance,
  balanceAnimation,
  walletAddress,
  onBalanceClick,
  onWalletToggle,
  showWalletView,
  isWalletView,
  walletContent,
  messageContent,
  timeRemaining,
  showConnectButton,
  onConnectClick,
  showGameHistory,
  setShowGameHistory,
  gameHistory,
  isFreePlayMode,
  autoBidEnabled,
  setAutoBidEnabled,
  setBidAmount,
  onWalletDepositClick,
  onWalletWithdrawClick,
  onWalletDepositsInfoClick,
  onWalletWithdrawalsInfoClick,
  onWalletSecurityInfoClick,
  onWalletReadMeCloseClick,
  isReadMeOpen,
  leaderboardData,
  leaderboardLoading,
  onLeaderboardClick,
  connectedPlayers,
  onWalletConnectClick,
  onWalletDisconnectClick,
  walletAmount,
  onWalletAmountChange,
  walletMode,
  walletOnChainBalance,
  walletPlatformBalance,
}) => {
  const { language, setLanguage, t } = useTranslation();
  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);
  const [winCelebrationId, setWinCelebrationId] = React.useState(0);
  const previousBalanceAnimationRef = React.useRef(balanceAnimation);
  const gridSize = 10;

  // When game finishes, show colors (stay visible until new game starts)
  const [showBidColors, setShowBidColors] = React.useState(false);
  const [displayNonWalletView, setDisplayNonWalletView] = React.useState(
    showGameHistory ? "history" : "game",
  );
  const [showLeaderboardView, setShowLeaderboardView] = React.useState(false);
  const [showProjectsView, setShowProjectsView] = React.useState(false);
  const [outgoingNonWalletView, setOutgoingNonWalletView] =
    React.useState(null);
  const [isViewTransitioning, setIsViewTransitioning] = React.useState(false);
  const [bidHoldAmount, setBidHoldAmount] = React.useState(null);
  const [bidHoldProgress, setBidHoldProgress] = React.useState(0);
  const [hoveredBidAmount, setHoveredBidAmount] = React.useState(null);
  const [sequentialHoveredBidAmount, setSequentialHoveredBidAmount] =
    React.useState(null);
  const [hasCompletedInitialGameFlash, setHasCompletedInitialGameFlash] =
    React.useState(false);
  const hadBidDistributionOnInitialRenderRef = React.useRef(
    Array.isArray(bidSummary) && bidSummary.length > 0,
  );
  const historyTransitionTimerRef = React.useRef(null);
  const bidHoldTimerRef = React.useRef(null);
  const bidHoldProgressIntervalRef = React.useRef(null);
  const bidHoldStartRef = React.useRef(0);
  const bidHoldFadeIntervalRef = React.useRef(null);
  const bidHoldAmountRef = React.useRef(null);
  const bidHoldProgressRef = React.useRef(0);
  const bidHoldTriggeredRef = React.useRef(false);
  const hasPlacedBidInCurrentGame =
    Boolean(submittedBid?.gameId) &&
    Boolean(currentGame?.id) &&
    submittedBid.gameId === currentGame.id;
  const isHoverEnabled =
    !hasPlacedBidInCurrentGame || hasCurrentGameBidDistribution;

  const targetNonWalletView = showProjectsView
    ? "projects"
    : showGameHistory
      ? showLeaderboardView
        ? "leaderboard"
        : "history"
      : "game";
  const bottomActionBlue = "var(--button-blue)";
  const bottomActionButtonBaseStyle = {
    borderRadius: "8px",
    padding: "10px 12px",
    minHeight: "36px",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    fontWeight: "700",
    flex: "1",
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    boxShadow: "none",
    filter: "none",
    transform: "none",
    transition: "none",
  };

  React.useEffect(() => {
    if (!winCelebrationId) {
      return undefined;
    }

    const timer = setTimeout(() => setWinCelebrationId(0), 2800);
    return () => clearTimeout(timer);
  }, [winCelebrationId]);

  React.useEffect(() => {
    if (
      balanceAnimation === "win" &&
      previousBalanceAnimationRef.current !== "win"
    ) {
      setWinCelebrationId((current) => current + 1);
    }

    previousBalanceAnimationRef.current = balanceAnimation;
  }, [balanceAnimation]);

  React.useEffect(() => {
    if (showWalletView) {
      if (historyTransitionTimerRef.current) {
        clearTimeout(historyTransitionTimerRef.current);
        historyTransitionTimerRef.current = null;
      }
      setOutgoingNonWalletView(null);
      setIsViewTransitioning(false);
      setDisplayNonWalletView(targetNonWalletView);
      return;
    }

    if (displayNonWalletView === targetNonWalletView) {
      return;
    }

    if (historyTransitionTimerRef.current) {
      clearTimeout(historyTransitionTimerRef.current);
      historyTransitionTimerRef.current = null;
    }

    setOutgoingNonWalletView(displayNonWalletView);
    setDisplayNonWalletView(targetNonWalletView);
    setIsViewTransitioning(true);

    historyTransitionTimerRef.current = setTimeout(() => {
      setOutgoingNonWalletView(null);
      setIsViewTransitioning(false);
      historyTransitionTimerRef.current = null;
    }, HISTORY_VIEW_TRANSITION_MS);
  }, [displayNonWalletView, showWalletView, targetNonWalletView]);

  React.useEffect(() => {
    bidHoldAmountRef.current = bidHoldAmount;
  }, [bidHoldAmount]);

  React.useEffect(() => {
    bidHoldProgressRef.current = bidHoldProgress;
  }, [bidHoldProgress]);

  React.useEffect(() => {
    if (isHoverEnabled) {
      return;
    }

    setHoveredBidAmount(null);
  }, [isHoverEnabled]);

  React.useEffect(() => {
    return () => {
      if (historyTransitionTimerRef.current) {
        clearTimeout(historyTransitionTimerRef.current);
        historyTransitionTimerRef.current = null;
      }

      if (bidHoldTimerRef.current) {
        clearTimeout(bidHoldTimerRef.current);
        bidHoldTimerRef.current = null;
      }

      if (bidHoldProgressIntervalRef.current) {
        clearInterval(bidHoldProgressIntervalRef.current);
        bidHoldProgressIntervalRef.current = null;
      }

      if (bidHoldFadeIntervalRef.current) {
        clearInterval(bidHoldFadeIntervalRef.current);
        bidHoldFadeIntervalRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!showLanguageSelector) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowLanguageSelector(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLanguageSelector]);

  React.useEffect(() => {
    if (!isNewGame) {
      // Game just finished - show colors and keep them visible
      setShowBidColors(true);
    } else if (autoBidEnabled && !isBidPlaced) {
      // Keep colors until auto-bid is placed so it can fade to orange
      setShowBidColors(true);
    } else {
      // New game started - hide colors
      setShowBidColors(false);
    }
  }, [isNewGame, currentGame, autoBidEnabled, isBidPlaced]);

  const handleLeaderboardClick = () => {
    if (!showGameHistory) {
      return;
    }

    setShowProjectsView(false);
    setShowLeaderboardView(true);
    onLeaderboardClick?.();
  };

  const handleTitleClick = () => {
    setShowProjectsView((previousValue) => !previousValue);
  };

  React.useEffect(() => {
    if (!showGameHistory) {
      setShowLeaderboardView(false);
    }
  }, [showGameHistory]);

  // Create a map for quick lookup
  const bidMap = {};
  if (bidSummary && bidSummary.length > 0) {
    bidSummary.forEach((bid) => {
      bidMap[bid.amount.toFixed(2)] = bid.count;
    });
  }

  const formatDisplayAmount = (amount) => {
    return formatBidAmount(amount);
  };

  const displayPot = potTotal || 0;
  const balanceAmount = typeof balance === "number" ? balance : 0;
  const amountStep = 0.1;
  const queuedBidAmount =
    pendingNextGameBid !== null && pendingNextGameBid !== undefined
      ? parseFloat(pendingNextGameBid)
      : null;

  const [potVisible, setPotVisible] = React.useState(displayPot > 0);

  React.useEffect(() => {
    if (displayPot > 0) {
      setPotVisible(true);
    }
  }, [displayPot]);

  // Determine font size based on grid size
  const getFontSize = () => {
    if (gridSize <= 36) return "16px";
    if (gridSize <= 49) return "12px";
    if (gridSize <= 64) return "10px";
    if (gridSize <= 81) return "9px";
    return "8px";
  };

  // Determine if we should show cents symbol
  // Generate grid points based on current grid size
  const gridPoints = React.useMemo(() => {
    const points = [];
    for (let i = 1; i <= gridSize; i += 1) {
      points.push((i * amountStep).toFixed(2));
    }
    return points;
  }, [gridSize, amountStep]);

  const fontSize = getFontSize();

  const isActiveGame = currentGame && isNewGame && (timeRemaining || 0) > 0;

  const requiresHighBidHold = (amountValue) =>
    !isFreePlayMode && amountValue > 0.5;

  const stopBidHoldAnimations = () => {
    if (bidHoldTimerRef.current) {
      clearTimeout(bidHoldTimerRef.current);
      bidHoldTimerRef.current = null;
    }

    if (bidHoldProgressIntervalRef.current) {
      clearInterval(bidHoldProgressIntervalRef.current);
      bidHoldProgressIntervalRef.current = null;
    }

    if (bidHoldFadeIntervalRef.current) {
      clearInterval(bidHoldFadeIntervalRef.current);
      bidHoldFadeIntervalRef.current = null;
    }
  };

  const fadeOutBidHold = () => {
    const currentAmount = bidHoldAmountRef.current;
    const startProgress = bidHoldProgressRef.current;

    if (currentAmount === null || startProgress <= 0) {
      setBidHoldAmount(null);
      setBidHoldProgress(0);
      return;
    }

    const fadeStart = Date.now();
    bidHoldFadeIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - fadeStart;
      const nextProgress = Math.max(
        0,
        startProgress * (1 - elapsed / HIGH_BID_FADE_OUT_MS),
      );
      setBidHoldProgress(nextProgress);

      if (nextProgress <= 0.001) {
        if (bidHoldFadeIntervalRef.current) {
          clearInterval(bidHoldFadeIntervalRef.current);
          bidHoldFadeIntervalRef.current = null;
        }
        setBidHoldProgress(0);
        setBidHoldAmount(null);
      }
    }, 16);
  };

  const clearBidHold = (preserveVisual = false) => {
    stopBidHoldAnimations();

    if (
      !preserveVisual &&
      !bidHoldTriggeredRef.current &&
      bidHoldAmountRef.current !== null
    ) {
      const heldForMs = Date.now() - bidHoldStartRef.current;
      if (heldForMs >= HIGH_BID_HOLD_MS) {
        bidHoldTriggeredRef.current = true;
        setBidHoldProgress(1);
        bidHoldProgressRef.current = 1;
        handleCellClick(bidHoldAmountRef.current);
      }
    }

    if (!preserveVisual && !bidHoldTriggeredRef.current) {
      fadeOutBidHold();
      return;
    }

    setBidHoldAmount(null);
    setBidHoldProgress(0);
  };

  const startBidHold = (amountValue) => {
    if (!requiresHighBidHold(amountValue) || (isBidPlaced && isActiveGame)) {
      return;
    }

    clearBidHold();
    setBidHoldAmount(amountValue);
    setBidHoldProgress(0);
    bidHoldAmountRef.current = amountValue;
    bidHoldProgressRef.current = 0;
    bidHoldStartRef.current = Date.now();
    bidHoldTriggeredRef.current = false;

    const holdStart = Date.now();
    bidHoldProgressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - holdStart;
      const progress = Math.min(1, elapsed / HIGH_BID_HOLD_MS);
      setBidHoldProgress(progress);

      if (progress >= 1) {
        if (bidHoldProgressIntervalRef.current) {
          clearInterval(bidHoldProgressIntervalRef.current);
          bidHoldProgressIntervalRef.current = null;
        }
      }
    }, 16);

    bidHoldTimerRef.current = setTimeout(() => {
      bidHoldTriggeredRef.current = true;
      setBidHoldProgress(1);
      bidHoldProgressRef.current = 1;
      handleCellClick(amountValue);
      clearBidHold(true);
    }, HIGH_BID_HOLD_MS);
  };

  const handleCellClick = (amountValue) => {
    const isQueuedClick =
      queuedBidAmount !== null &&
      Math.abs(queuedBidAmount - amountValue) < 0.001;

    if (!isActiveGame) {
      if (isQueuedClick) {
        if (onBidSubmit) {
          onBidSubmit(null, { cancelQueue: true });
        }
        return;
      }

      if (onBidSubmit) {
        onBidSubmit(amountValue, { forceQueue: true });
      }
      return;
    }

    // Prevent bid changes after submitting a bid for current game
    if (isBidPlaced && currentGame) {
      return;
    }

    // If autobid is enabled, update the bid amount for next game
    if (autoBidEnabled && setBidAmount) {
      setBidAmount(amountValue);
    }

    // Submit bid immediately with the clicked amount
    if (onBidSubmit) {
      onBidSubmit(amountValue);
    }
  };

  // Generate shareable result text
  const generateShareText = () => {
    if (!lowestWinningBid || !lastBidPlaced) return "";

    const didWin =
      Math.abs(parseFloat(lastBidPlaced) - lowestWinningBid) < 0.001;
    if (!didWin) return "";

    const potFormatted = formatDisplayAmount(potTotal);
    const bidFormatted = formatDisplayAmount(lastBidPlaced);

    let text = `Bid Arenas 🎯\n${potFormatted} pot won with ${bidFormatted} bid\n\n`;

    // Generate 5x5 grid with emojis
    gridPoints.forEach((amount, index) => {
      const count = bidMap[amount] || 0;
      const isWinningBid =
        Math.abs(parseFloat(amount) - lowestWinningBid) < 0.001;

      let emoji;
      if (isWinningBid) {
        emoji = "🟩"; // Green for winning bid
      } else if (count === 0) {
        emoji = "⬜"; // Gray for no bids
      } else if (count === 1) {
        emoji = "🔷"; // Light blue for 1 bid
      } else if (count === 2) {
        emoji = "🔵"; // Medium blue for 2 bids
      } else {
        emoji = "🟦"; // Dark blue for 3+ bids
      }

      text += emoji;

      // Add newline after each row
      if ((index + 1) % 5 === 0) {
        text += "\n";
      }
    });

    return text;
  };

  // Handle share button click
  const handleShare = async () => {
    const shareText = generateShareText();
    if (!shareText) return;

    try {
      await navigator.clipboard.writeText(shareText);
      alert(t("game.copySuccess"));
    } catch (err) {
      console.error("Failed to copy:", err);
      alert(t("game.copyFailure"));
    }
  };

  // Check if user won
  const userWon =
    lowestWinningBid &&
    lastBidPlaced &&
    Math.abs(parseFloat(lastBidPlaced) - lowestWinningBid) < 0.001;

  // Responsive padding for small screens
  const [containerPadding, setContainerPadding] = React.useState("16px");

  React.useEffect(() => {
    const updatePadding = () => {
      // Use 16px padding consistently
      setContainerPadding("16px");
    };

    updatePadding();
    window.addEventListener("resize", updatePadding);
    return () => window.removeEventListener("resize", updatePadding);
  }, []);

  const realPlayers = Math.max(0, Math.floor(connectedPlayers || 0));
  const autobidderCount = Math.max(3, 4 - realPlayers);
  const gameGridPoints = React.useMemo(() => gridPoints.slice(0, 10), [gridPoints]);
  const gameGridColumns = 5;
  const hasBidHistoryRows =
    Array.isArray(gameHistory) && gameHistory.length > 0;
  const shouldRunInitialGameFlash =
    !hadBidDistributionOnInitialRenderRef.current &&
    !hasCompletedInitialGameFlash;

  React.useEffect(() => {
    if (!shouldRunInitialGameFlash || gameGridPoints.length === 0) {
      setSequentialHoveredBidAmount(null);
      return;
    }

    const singlePass = gameGridPoints.map((amount) => parseFloat(amount));
    const sequence = Array.from(
      { length: INITIAL_GAME_FLASH_REPETITIONS },
      () => singlePass,
    ).flat();
    const stepMs = Math.max(
      INITIAL_GAME_FLASH_MIN_STEP_MS,
      Math.floor(INITIAL_GAME_FLASH_PASS_MS / singlePass.length),
    );
    let index = 0;

    setSequentialHoveredBidAmount(sequence[index]);

    const intervalId = setInterval(() => {
      index += 1;
      if (index >= sequence.length) {
        clearInterval(intervalId);
        setSequentialHoveredBidAmount(null);
        setHasCompletedInitialGameFlash(true);
        return;
      }

      setSequentialHoveredBidAmount(sequence[index]);
    }, stepMs);

    return () => {
      clearInterval(intervalId);
      setSequentialHoveredBidAmount(null);
    };
  }, [gameGridPoints, shouldRunInitialGameFlash]);

  const renderGameLegend = () => (
    <div
      className="game-grid-legend"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
        fontSize: "12px",
        color: "#666",
        height: "20px",
        opacity: hasBidHistoryRows ? 1 : 0,
        pointerEvents: hasBidHistoryRows ? "auto" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{ display: "flex", gap: "2px" }}>
            {[0.25, 0.6, 1].map((opacity) => (
              <div
                key={opacity}
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: `rgba(59, 130, 246, ${opacity})`,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <span>{t("game.bids")}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "14px",
              height: "14px",
              backgroundColor: "#4ade80",
              borderRadius: "2px",
              flexShrink: 0,
            }}
          />
          <span>{t("game.lowestUniqueBid")}</span>
        </div>
      </div>
      {displayGameId && (
        <span style={{ color: "#999", fontSize: "12px" }}>
          {displayGameId.substring(0, 8)}
        </span>
      )}
    </div>
  );

  const renderPotBalance = () => {
    return (
      <div
        className="bid-grid-pot"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexShrink: 0,
          color: "#000",
          fontSize: "16px",
          fontWeight: "bold",
          whiteSpace: "nowrap",
        }}
      >
        <span>{t("game.pot")}</span>
        <span
          className="money-value pot-money-value"
          style={{
            fontVariantNumeric: "tabular-nums",
            fontFamily: "Inter, sans-serif",
            fontFeatureSettings: '"tnum"',
            opacity: displayPot > 0 && potVisible ? 1 : 0,
          }}
        >
          <MoneyOdometerValue amount={displayPot} duration={1000} />
        </span>
      </div>
    );
  };

  const renderGameView = () => (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        gap: "16px",
      }}
    >
      <style>
        {`@keyframes queuedBidFill {
  from { background-color: rgba(255, 149, 0, 0); }
  to { background-color: #FF9200; }
}

@keyframes queuedAutoBidFill {
  from { background-color: rgba(255, 149, 0, 0); }
  to { background-color: rgba(255, 149, 0, 0.5); }
}`}
      </style>
      {renderGameLegend()}
      <HistoryGrid
        gameHistory={gameHistory}
        isFreePlayMode={isFreePlayMode}
        maxRows={6}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div
          className="bid-grid-message"
          style={{
            height: "20px",
            display: "flex",
            alignItems: "center",
            lineHeight: "20px",
            flexShrink: 0,
            color: "#666",
          }}
        >
          {messageContent}
        </div>
        <div
          className="bid-grid-timer"
          style={{
            width: "100%",
            height: "22px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {renderPotBalance()}
          <div
            className="bid-grid-timer-track"
            style={{
              flex: "1",
              minWidth: 0,
              height: "14px",
              backgroundColor: "transparent",
              borderRadius: "2px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                width:
                  isNewGame && timeRemaining > 0
                    ? `${Math.min(100, (timeRemaining / 10) * 100)}%`
                    : "0%",
                height: "100%",
                backgroundColor: "#4ade80",
                transition: isNewGame ? "width 1s linear" : "none",
                borderRadius: "2px",
              }}
            />
          </div>
        </div>
        <div
          className="bid-grid"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${gameGridColumns}, 1fr)`,
            gap: "2px",
          }}
        >
          {gameGridPoints.map((amount) => {
          const count = bidMap[amount] || 0;
          const isWinningBid =
            lowestWinningBid &&
            Math.abs(parseFloat(amount) - lowestWinningBid) < 0.001;

          const amountValue = parseFloat(amount);
          const displayAmount = `${Math.round(amountValue * 100)}¢`;

          let backgroundColor;
          let backgroundTransition;
          let backgroundAnimation;
          const autoBidCellColor = "rgba(255, 149, 0, 0.5)";

          const submittedBidAmount = submittedBid?.amount;
          const submittedBidGameId = submittedBid?.gameId;
          const isSubmittedBidActive =
            submittedBidAmount !== null &&
            submittedBidAmount !== undefined &&
            submittedBidGameId &&
            currentGame?.id &&
            submittedBidGameId === currentGame.id;

          const isPlacedBidCell =
            isSubmittedBidActive &&
            Math.abs(parseFloat(amount) - parseFloat(submittedBidAmount)) <
              0.001;

          const isQueuedBidCell =
            queuedBidAmount !== null &&
            Math.abs(parseFloat(amount) - queuedBidAmount) < 0.001;
          const isManualQueuedBidCell =
            isQueuedBidCell && !isPlacedBidCell && !autoBidEnabled;

          const isQueuedToPlaced =
            isPlacedBidCell &&
            queuedBidAmount !== null &&
            Math.abs(parseFloat(submittedBidAmount) - queuedBidAmount) < 0.001;

          const isHoldingBidCell =
            bidHoldAmount !== null &&
            bidHoldProgress > 0 &&
            Math.abs(parseFloat(amount) - bidHoldAmount) < 0.001;
          const isHoveredBidCell =
            ((isHoverEnabled &&
              hoveredBidAmount !== null &&
              Math.abs(parseFloat(amount) - hoveredBidAmount) < 0.001) ||
              (sequentialHoveredBidAmount !== null &&
                Math.abs(parseFloat(amount) - sequentialHoveredBidAmount) <
                  0.001));

          if (isHoldingBidCell && !isPlacedBidCell) {
            backgroundColor = `rgba(255, 146, 0, ${bidHoldProgress})`;
            backgroundTransition = "background-color 0s";
            backgroundAnimation = null;
          } else if (isManualQueuedBidCell) {
            backgroundColor = "rgba(251, 139, 30, 0)";
            backgroundTransition = "background-color 0s";
            backgroundAnimation = "queuedBidFill 3s forwards";
          } else if (showBidColors) {
            if (isWinningBid) {
              backgroundColor = "#4ade80";
            } else if (count === 1) {
              backgroundColor = "rgba(59, 130, 246, 0.25)";
            } else if (count === 2) {
              backgroundColor = "rgba(59, 130, 246, 0.6)";
            } else if (count >= 3) {
              backgroundColor = "rgba(59, 130, 246, 1)";
            } else {
              backgroundColor = "#e5e5e5";
            }
            backgroundTransition = "background-color 2s ease";
          } else if (isQueuedBidCell && !isPlacedBidCell) {
            backgroundColor = autoBidEnabled
              ? "rgba(255, 149, 0, 0)"
              : "rgba(251, 139, 30, 0)";
            backgroundTransition = "background-color 0s";
            backgroundAnimation = autoBidEnabled
              ? "queuedAutoBidFill 3s forwards"
              : "queuedBidFill 3s forwards";
          } else if (isPlacedBidCell && (isActiveGame || !showBidColors)) {
            backgroundColor = autoBidEnabled ? autoBidCellColor : "#FF9200";
            backgroundTransition = isQueuedToPlaced
              ? "background-color 1s ease"
              : autoBidEnabled
                ? "background-color 2s ease"
                : "background-color 150ms ease";
          } else {
            backgroundColor = "#e5e5e5";
            backgroundTransition = "background-color 2s ease";
          }

          if (isHoveredBidCell) {
            backgroundColor = "#fac578";
          }

          // Keep hover fade-in/fade-out consistent at 200ms.
          backgroundTransition = "background-color 200ms ease";

          return (
            <div
              key={amount}
              onMouseDown={() => startBidHold(amountValue)}
              onMouseUp={clearBidHold}
              onMouseEnter={() => {
                if (!isHoverEnabled) {
                  return;
                }

                setHoveredBidAmount(amountValue);
              }}
              onMouseLeave={() => {
                setHoveredBidAmount(null);
                clearBidHold();
              }}
              onTouchStart={() => startBidHold(amountValue)}
              onTouchEnd={clearBidHold}
              onTouchCancel={clearBidHold}
              onClick={() => {
                if (requiresHighBidHold(amountValue)) {
                  if (bidHoldTriggeredRef.current) {
                    bidHoldTriggeredRef.current = false;
                    clearBidHold();
                    return;
                  }

                  return;
                }

                handleCellClick(amountValue);
              }}
              style={{
                backgroundColor: backgroundColor,
                borderRadius: "2px",
                transition: backgroundTransition || "background-color 200ms ease",
                animation: backgroundAnimation,
                aspectRatio: "1 / 1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: fontSize,
                fontWeight: "600",
                color: "#333",
                cursor: "pointer",
                userSelect: "none",
                WebkitUserSelect: "none",
                msUserSelect: "none",
              }}
            >
              {displayAmount}
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );

  const renderNonWalletView = (view) => {
    if (view === "projects") {
      return <ProjectsView />;
    }

    if (view === "leaderboard") {
      return (
        <LeaderboardView
          leaderboardData={leaderboardData}
          leaderboardLoading={leaderboardLoading}
        />
      );
    }

    if (view === "history") {
      return (
        <HistoryGrid
          gameHistory={gameHistory}
          isFreePlayMode={isFreePlayMode}
        />
      );
    }

    return renderGameView();
  };

  const nonWalletPanelHeight =
    displayNonWalletView === "game" || outgoingNonWalletView === "game"
      ? "100%"
      : "308px";

  return (
    <div
      className="bid-distribution-layout"
      style={{
        padding: containerPadding,
        backgroundColor: "#ffffff",
        borderRadius: "24px",
        marginBottom: "0px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {winCelebrationId > 0 && (
        <div
          key={winCelebrationId}
          className="win-celebration"
          aria-hidden="true"
        >
          <div className="win-celebration-coins">
            {Array.from({ length: 42 }, (_, index) => (
              <span
                key={index}
                style={{
                  "--particle-left": `${2 + ((index * 37) % 96)}%`,
                  "--particle-delay": `${(index % 12) * 0.055}s`,
                  "--particle-drift": `${((index % 7) - 3) * 15}px`,
                  "--particle-size": `${19 + (index % 5) * 4}px`,
                  "--particle-duration": `${1.8 + (index % 6) * 0.12}s`,
                  "--particle-spin": `${(index % 2 ? -1 : 1) * (720 + (index % 4) * 180)}deg`,
                }}
              >
                $
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header top row - 2 columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        {/* Bid Arenas title */}
        <button
          type="button"
          onClick={handleTitleClick}
          style={{
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
            display: "flex",
            alignItems: "center",
            textAlign: "left",
            height: "24px",
            fontFamily:
              '"Google Sans", "Google Sans Text", "Product Sans", sans-serif',
            fontSize: "20px",
            fontWeight: "700",
            fontVariationSettings: '"GRAD" 200',
            letterSpacing: "0.2px",
            textTransform: "none",
            color: "var(--button-blue)",
            background: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Bid Arenas
          {isFreePlayMode && !walletAddress
            ? ` ${t("game.freePlay")}`
            : ""}
        </button>

        <div
          style={{
            gridColumn: "2 / 3",
            gridRow: "1 / 2",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            height: "24px",
          }}
        >
          {(() => {
            const showControl = !isWalletView && !!walletAddress;

            return (
          <button
            type="button"
            className="header-nav-text"
            style={{
              color: "var(--button-blue)",
              background: "transparent",
              border: "none",
              borderRadius: 0,
              padding: 0,
              margin: 0,
              fontWeight: "600",
              fontSize: "12px",
              textDecoration: "none",
              visibility: showControl ? "visible" : "hidden",
              pointerEvents: showControl ? "auto" : "none",
            }}
            onClick={onWalletToggle}
            tabIndex={showControl ? 0 : -1}
            aria-hidden={!showControl}
          >
            {walletAddress
              ? `${walletAddress.substring(0, 6)}...${walletAddress.substring(
                  walletAddress.length - 4,
                )}`
              : ""}
          </button>
            );
          })()}
        </div>

      </div>

      <div className="game-status-header">
      {/* Header stats rows - 2 columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "auto",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >

        {/* Balance */}
        <div
          style={{
            gridColumn: "2 / 3",
            gridRow: "1 / 2",
            color: "#000",
            textAlign: "left",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: walletAddress ? "pointer" : "default",
              opacity: 1,
              transition: "opacity 0.2s ease",
            }}
            onClick={onBalanceClick}
            onMouseEnter={(e) =>
              walletAddress && (e.currentTarget.style.opacity = "0.7")
            }
            onMouseLeave={(e) =>
              walletAddress && (e.currentTarget.style.opacity = "1")
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                paddingLeft: "3px",
              }}
            >
              {t("game.balance")}
            </div>
            <div
              className="money-value balance-money-value"
              style={{
                fontVariantNumeric: "tabular-nums",
                fontFamily: "Inter, sans-serif",
                fontFeatureSettings: '"tnum"',
                color: "#000",
              }}
            >
              <MoneyOdometerValue amount={balanceAmount} />
            </div>
          </div>
        </div>

        {/* Connected bots and real players */}
        <div
          className="connected-player-counts"
          style={{
            gridColumn: "1 / 2",
            gridRow: "1 / 2",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            fontWeight: "bold",
            color: "#00A368",
            letterSpacing: "0.5px",
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
        >
          <span
            className="participant-count"
            aria-label={`${autobidderCount} bot players`}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <span aria-hidden="true">🤖</span>
            <span
              className="participant-count-number"
              style={{ color: "#000", fontSize: "16px" }}
            >
              <OdometerValue value={autobidderCount} duration={1000} />
            </span>
          </span>
          <span
            className="participant-count"
            aria-label={`${realPlayers} real players`}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <span aria-hidden="true">👤</span>
            <span
              className="participant-count-number"
              style={{ color: "#000", fontSize: "16px" }}
            >
              <OdometerValue value={realPlayers} duration={1000} />
            </span>
          </span>
        </div>
      </div>

      </div>

      <div
        className={`bid-distribution-center${
          showWalletView ? " wallet-open" : ""
        }`}
      >
      {/* Conditional rendering: Wallet view, Game view, or History view */}
      {showWalletView ? (
        <div
          className="wallet-view-panel"
          style={{
            width: "min(100%, 480px)",
            margin: "0 auto",
          }}
        >
          {walletContent}
        </div>
      ) : (
        <div
          style={{
            width: "min(100%, 480px)",
            height: nonWalletPanelHeight,
            margin: "0 auto",
            position: "relative",
            overflow:
              displayNonWalletView === "game" ||
              outgoingNonWalletView === "game"
                ? "visible"
                : "hidden",
          }}
        >
          <style>
            {`@keyframes historyViewFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes historyViewFadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}`}
          </style>
          {isViewTransitioning && outgoingNonWalletView && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                animation: `historyViewFadeOut ${HISTORY_VIEW_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1) forwards`,
              }}
            >
              {renderNonWalletView(outgoingNonWalletView)}
            </div>
          )}
          <div
            style={
              isViewTransitioning
                ? {
                    position: "absolute",
                    inset: 0,
                    animation: `historyViewFadeIn ${HISTORY_VIEW_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1) forwards`,
                  }
                : {
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }
            }
          >
            {renderNonWalletView(displayNonWalletView)}
          </div>
        </div>
      )}

      {!showWalletView &&
        displayNonWalletView !== "game" &&
        renderGameLegend()}

      {/* Share Result button - only show when user won and colors are showing */}
      {showBidColors && userWon && !currentGame && (
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleShare}
            style={{
              background: "#4ade80",
              border: "1px solid #22c55e",
              borderRadius: "4px",
              padding: "6px 16px",
              color: "#fff",
              cursor: "pointer",
              fontSize: "14px",
              fontFamily: "inherit",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            📋 {t("game.shareResult")}
          </button>
        </div>
      )}
      </div>

      {/* History and Auto-Bid buttons */}
      <footer
        className="game-bottom-actions"
        style={{
          marginTop: "16px",
          fontSize: "12px",
          display: "flex",
          gap: "16px",
        }}
      >
        <button
          onClick={
            showWalletView
              ? isReadMeOpen
                ? () => onWalletReadMeCloseClick?.()
                : walletAddress
                  ? onWalletToggle
                  : () => onWalletConnectClick?.()
              : onWalletToggle
          }
          style={{
            ...bottomActionButtonBaseStyle,
            background: bottomActionBlue,
            border: `1px solid ${bottomActionBlue}`,
          }}
        >
          <span style={{ position: "relative", zIndex: 1 }}>
            {showWalletView
              ? isReadMeOpen
                ? t("game.wallet")
                : walletAddress
                  ? t("game.game")
                  : t("game.connect")
              : t("game.wallet")}
          </span>
        </button>
        <button
          onClick={
            showWalletView
              ? walletAddress
                ? () => onWalletDisconnectClick?.()
                : onWalletToggle
              : showGameHistory
                ? showLeaderboardView
                  ? () => setShowLeaderboardView(false)
                  : handleLeaderboardClick
                : () => {
                    setWinCelebrationId((current) => current + 1);
                    setAutoBidEnabled?.(!autoBidEnabled);
                  }
          }
          style={{
            ...bottomActionButtonBaseStyle,
            background:
              !showWalletView && !showGameHistory && autoBidEnabled
                ? "rgba(255, 149, 0, 0.5)"
                : bottomActionBlue,
            border: `1px solid ${
              !showWalletView && !showGameHistory && autoBidEnabled
                ? "rgba(255, 149, 0, 0.8)"
                : bottomActionBlue
            }`,
          }}
        >
          <span style={{ position: "relative", zIndex: 1 }}>
            {showWalletView
              ? walletAddress
                ? t("game.disconnect")
                : t("game.game")
              : showGameHistory
                ? showLeaderboardView
                  ? t("game.history")
                  : t("game.leaderboard")
                : t("game.autoBid")}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setShowLanguageSelector(true)}
          style={{
            ...bottomActionButtonBaseStyle,
            background: bottomActionBlue,
            border: `1px solid ${bottomActionBlue}`,
          }}
          aria-label={t("language.select")}
        >
          <span
            style={{
              position: "relative",
              zIndex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              lineHeight: 1,
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: "1em", height: "1em", flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18" />
              <path d="M12 3c2.5 2.5 3.75 5.5 3.75 9S14.5 18.5 12 21" />
              <path d="M12 3C9.5 5.5 8.25 8.5 8.25 12S9.5 18.5 12 21" />
            </svg>
            <span>{language.toUpperCase()}</span>
          </span>
        </button>
      </footer>

      {showLanguageSelector && (
        <div
          className="language-modal-overlay"
          onClick={() => setShowLanguageSelector(false)}
        >
          <div
            className="language-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="language-modal-header">
              <h2 id="language-modal-title">{t("language.select")}</h2>
              <button
                type="button"
                className="language-modal-close"
                onClick={() => setShowLanguageSelector(false)}
                aria-label={t("language.close")}
              >
                ×
              </button>
            </div>
            <div className="language-modal-options">
              {[
                ["en", "English"],
                ["fr", "Français"],
                ["es", "Español"],
              ].map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  className={`language-modal-option${
                    language === code ? " active" : ""
                  }`}
                  onClick={() => {
                    setLanguage(code);
                    setShowLanguageSelector(false);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// History Grid Component (shows last 10 games, 10 cells per game)
const HistoryGrid = ({ gameHistory, isFreePlayMode, maxRows = 10 }) => {
  const { t } = useTranslation();
  // Responsive padding for small screens
  const [containerPadding, setContainerPadding] = React.useState("16px");
  const rowGapPx = 2;
  const hasHistoryDistribution = Array.isArray(gameHistory) && gameHistory.length > 0;

  React.useEffect(() => {
    const updatePadding = () => {
      // Reduce padding on small screens (iPhone SE: 375px)
      if (window.innerWidth <= 400) {
        setContainerPadding("0px");
      } else {
        setContainerPadding("0px");
      }
    };

    updatePadding();
    window.addEventListener("resize", updatePadding);
    return () => window.removeEventListener("resize", updatePadding);
  }, []);

  // Helper: Get cell color for history view
  const getCellColor = (amount, count, realLwb, freeLwb) => {
    const targetLwb = isFreePlayMode ? freeLwb : realLwb;
    if (
      targetLwb !== null &&
      targetLwb !== undefined &&
      Math.abs(amount - targetLwb) < 0.001
    ) {
      return "#4ade80";
    }

    // Blue with fixed opacity for bids (25% for 1, 60% for 2, 100% for 3+)
    if (count === 1) {
      return "rgba(59, 130, 246, 0.25)";
    } else if (count === 2) {
      return "rgba(59, 130, 246, 0.6)";
    } else if (count >= 3) {
      return "rgba(59, 130, 246, 1)";
    }

    return "transparent";
  };

  const getBidMap = (game) => {
    const bidSummaryToUse = isFreePlayMode
      ? game?.combined_bid_summary
      : game?.bid_summary;
    const bidMap = {};

    if (bidSummaryToUse && bidSummaryToUse.length > 0) {
      bidSummaryToUse.forEach((bid) => {
        bidMap[bid.amount.toFixed(2)] = bid.count;
      });
    }

    return bidMap;
  };

  const getDisplayPot = (game) => {
    if (!game) {
      return null;
    }

    const rawPot = isFreePlayMode
      ? game.combined_pot_total
      : game.real_pot_total;
    const numericPot = Number(rawPot);
    if (Number.isFinite(numericPot)) {
      return numericPot;
    }

    const bidSummaryToUse = isFreePlayMode
      ? game?.combined_bid_summary
      : game?.bid_summary;

    if (!Array.isArray(bidSummaryToUse)) {
      return null;
    }

    const computedPot = bidSummaryToUse.reduce((sum, bid) => {
      const amount = Number(bid?.amount);
      const count = Number(bid?.count);
      if (!Number.isFinite(amount) || !Number.isFinite(count)) {
        return sum;
      }

      return sum + amount * count;
    }, 0);

    return Number.isFinite(computedPot) ? computedPot : null;
  };

  const emptyRowHints = {
    1: t("history.submitBid"),
    2: t("history.bidsEnterPot"),
    3: t("history.lowestWins"),
    4: t("history.previousBids"),
    5: t("history.potRolls"),
  };
  const showDefaultEmptyRowHints = maxRows === 6;

  const getGameDescription = (game) => {
    const pot = getDisplayPot(game);
    if (!game || !Number.isFinite(pot)) {
      return null;
    }

    const lwbRaw = isFreePlayMode
      ? game.free_track_lwb
      : game.lowest_winning_bid_real;
    const lwb = Number(lwbRaw);
    const hasWinningBid =
      lwbRaw !== null && lwbRaw !== undefined && Number.isFinite(lwb);

    if (hasWinningBid) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25ch",
          }}
        >
          <span style={{ fontWeight: "700" }}>{formatBidAmount(pot)}</span>
          <span>{t("history.potWonBy")}</span>
          <span style={{ fontWeight: "700" }}>{formatBidAmount(lwb)}</span>
          <span>{t("history.lowestUniqueBid")}</span>
        </span>
      );
    }

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.25ch",
        }}
      >
        <span>{t("history.noUniqueBids")}</span>
        <span style={{ fontWeight: "700" }}>{formatBidAmount(pot)}</span>
        <span>{t("history.potRollsOver")}</span>
      </span>
    );
  };

  return (
    <div
      className="bid-history-grid"
      style={{
        width: "100%",
        padding: containerPadding,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(10, 1fr)",
          gridTemplateRows: `repeat(${maxRows}, 1fr)`,
          gap: "2px",
          position: "relative",
        }}
      >
        {Array.from({ length: maxRows })
          .map((_, rowIndex) => {
            const amountStep = 0.1;
            const game = gameHistory[rowIndex] || null;

            const bidMap = game ? getBidMap(game) : {};

            // Generate cells for this game ($1-$10)
            return Array.from({ length: 10 }).map((_, colIndex) => {
              const amountValue = (colIndex + 1) * amountStep;
              const amount = amountValue.toFixed(2);

              if (!game) {
                // Empty cell if no game data for this row
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    style={{
                      backgroundColor: "transparent",
                      opacity: 1,
                      borderRadius: "2px",
                      aspectRatio: "1 / 1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Inter, sans-serif",
                      cursor: "default",
                      transition: "background-color 0.3s ease",
                    }}
                  />
                );
              }

              const count = bidMap[amount] || 0;
              const baseColor = getCellColor(
                parseFloat(amount),
                count,
                game.lowest_winning_bid_real,
                game.free_track_lwb,
              );

              const userWinningAmount = isFreePlayMode
                ? game.free_track_lwb
                : game.lowest_winning_bid_real;

              const isUserWinningBidCell =
                game.user_bid_amount !== null &&
                userWinningAmount !== null &&
                Math.abs(game.user_bid_amount - userWinningAmount) < 0.001 &&
                Math.abs(parseFloat(amount) - userWinningAmount) < 0.001;

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    backgroundColor: baseColor,
                    borderRadius: "2px",
                    aspectRatio: "1 / 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter, sans-serif",
                    cursor: "default",
                    transition: "background-color 0.3s ease",
                    color: isUserWinningBidCell ? "#ffffff" : "transparent",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {isUserWinningBidCell ? "$" : ""}
                </div>
              );
            });
          })
          .flat()}
        {showDefaultEmptyRowHints
          ? Object.entries(emptyRowHints).map(([rowIndexKey, hintText]) => {
              const rowIndex = Number(rowIndexKey);

              if (rowIndex >= maxRows || gameHistory[rowIndex]) {
                return null;
              }

              return (
                <div
                  key={`default-hint-${rowIndex}`}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `calc(${rowIndex} * ((100% - ${(maxRows - 1) * rowGapPx}px) / ${maxRows} + ${rowGapPx}px))`,
                    height: `calc((100% - ${(maxRows - 1) * rowGapPx}px) / ${maxRows})`,
                    borderRadius: "2px",
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#666",
                    padding: "0 8px",
                    pointerEvents: "none",
                    zIndex: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25ch",
                    }}
                  >
                    <span>{hintText}</span>
                  </span>
                </div>
              );
            })
          : null}
        {hasHistoryDistribution
          ? gameHistory.slice(0, maxRows).map((game, rowIndex) => {
              const description = getGameDescription(game);
              if (!description) {
                return null;
              }

              return (
                <div
                  key={`game-description-${rowIndex}`}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `calc(${rowIndex} * ((100% - ${(maxRows - 1) * rowGapPx}px) / ${maxRows} + ${rowGapPx}px))`,
                    height: `calc((100% - ${(maxRows - 1) * rowGapPx}px) / ${maxRows})`,
                    borderRadius: "2px",
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "#666",
                    padding: "0 8px",
                    pointerEvents: "none",
                    opacity: 1,
                    zIndex: 3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {description}
                </div>
              );
            })
          : null}
      </div>
    </div>
  );
};

const abbreviateWalletAddress = (walletAddress) => {
  if (!walletAddress || walletAddress.length < 12) {
    return walletAddress || "-";
  }

  return `${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`;
};

const formatWinnings = (amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "$0";
  }

  return `$${numericAmount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ProjectsView = () => {
  const getFaviconSources = (projectUrl) => {
    try {
      const parsedUrl = new URL(projectUrl);
      const domain = parsedUrl.hostname;
      return {
        primary: `${parsedUrl.origin}/favicon.ico`,
        fallback: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
      };
    } catch {
      return {
        primary: "",
        fallback: "",
      };
    }
  };

  const projects = [
    {
      name: "ASC11",
      url: "https://asc11.com/",
      description: "ASCII editor for art, video, live cam, and WebGL exports.",
    },
    {
      name: "STFU",
      url: "https://stfu.pankajtanwar.in/",
      description: "Tap to shut them up.",
    },
  ];

  return (
    <div
      style={{
        width: "308px",
        height: "308px",
        margin: "0 auto",
        padding: "8px",
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {projects.map((project) => (
          <div
            key={project.url}
            style={{
              padding: "2px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "var(--button-blue)",
                  fontSize: "14px",
                  fontWeight: "700",
                  textDecoration: "none",
                }}
              >
                {project.name}
              </a>
              <img
                src={getFaviconSources(project.url).primary}
                alt=""
                style={{ width: "16px", height: "16px", flexShrink: 0 }}
                loading="lazy"
                onError={(event) => {
                  const fallback = getFaviconSources(project.url).fallback;
                  if (fallback && event.currentTarget.src !== fallback) {
                    event.currentTarget.src = fallback;
                    return;
                  }

                  event.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div
              style={{
                marginTop: "6px",
                fontSize: "13px",
                color: "#555",
                lineHeight: "1.4",
              }}
            >
              {project.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LeaderboardView = ({ leaderboardData, leaderboardLoading }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        padding: "6px 0",
      }}
    >
      {leaderboardLoading ? (
        <div
          style={{
            color: "#666",
            fontSize: "13px",
            textAlign: "center",
            marginTop: "12px",
          }}
        >
          {t("leaderboard.loading")}
        </div>
      ) : !leaderboardData || leaderboardData.length === 0 ? (
        <div
          style={{
            color: "#666",
            fontSize: "13px",
            textAlign: "center",
            marginTop: "12px",
          }}
        >
          {t("leaderboard.empty")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {leaderboardData.map((entry, index) => (
            <div
              key={`${entry.walletAddress}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "30px 1fr auto",
                alignItems: "center",
                gap: "8px",
                border: "1px solid #e1e1de",
                borderRadius: "6px",
                padding: "8px 10px",
                fontSize: "13px",
                color: "#333",
                backgroundColor: "#fff",
              }}
            >
              <span style={{ color: "#666", fontWeight: "600" }}>
                {entry.rank || index + 1}
              </span>
              <span
                style={{ fontFamily: "Inter, sans-serif", fontWeight: "500" }}
              >
                {abbreviateWalletAddress(entry.walletAddress)}
              </span>
              <span style={{ color: "var(--button-blue)", fontWeight: "700" }}>
                {formatWinnings(entry.totalWinnings)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BidDistributionChart;
